import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { COLLECTIONS, collection } from 'fb';
import { useTranslation } from 'react-i18next';
import { User } from 'types';

export const USERS_QUERY_KEY = 'users';

export const useUserById = (id: string) => {
  const { t } = useTranslation(['common']);
  return useQuery({
    queryKey: [USERS_QUERY_KEY, id],
    queryFn: () =>
      collection<User>(COLLECTIONS.USERS)
        .doc(id)
        .get()
        .then((user) => {
          if (user.exists) {
            return user;
          } else {
            throw Error(t('USERS.NOTFOUND'));
          }
        }),
    enabled: id !== '',
  });
};

export const useUserByEmail = (email: string) => {
  const { t } = useTranslation(['common']);
  return useQuery({
    queryKey: [USERS_QUERY_KEY, email],
    queryFn: () =>
      collection<User>(COLLECTIONS.USERS)
        .where('email', '==', email)
        .get()
        .then((users) => {
          if (!users.empty && users.docs[0].exists) {
            return users.docs[0];
          } else {
            throw Error(t('USERS.NOTFOUND'));
          }
        }),
    enabled: email !== '',
  });
};

export const useUsersUtils = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation(['common']);

  const getUserById = useCallback(
    async (id: string, refetch = false) => {
      const user = await queryClient.fetchQuery({
        queryKey: [USERS_QUERY_KEY, id],
        queryFn: () => collection<User>(COLLECTIONS.USERS).doc(id).get(),
        ...(refetch ? { staleTime: 1 } : {}),
      });
      if (user.exists) {
        return user;
      } else {
        throw Error(t('USERS.NOTFOUND'));
      }
    },
    [queryClient],
  );

  const getUserByEmail = useCallback(
    async (email: string) => {
      return queryClient.fetchQuery({
        queryKey: [USERS_QUERY_KEY, email],
        queryFn: () =>
          collection<User>(COLLECTIONS.USERS)
            .where('email', '==', email)
            .get()
            .then((users) => {
              if (!users.empty && users.docs[0].exists) {
                return users.docs[0];
              } else {
                throw Error(t('USERS.NOTFOUND'));
              }
            }),
      });
    },
    [queryClient],
  );

  const getAllChildDocByParentId = useCallback(
    async (id: string) => {
      const user = await queryClient.fetchQuery({
        queryKey: [USERS_QUERY_KEY, id],
        queryFn: () => collection<User>(COLLECTIONS.USERS).doc(id).get(),
      });
      if (user.exists) {
        return user;
      } else {
        throw Error(t('USERS.NOTFOUND'));
      }
    },
    [queryClient],
  );

  return { getUserById, getUserByEmail };
};
